import { BaseSyntheticEvent, useState } from "react";
import { TextField } from "@inductiveautomation/ws-components";
import { useTranslation } from "react-i18next";

import { Profile } from "../Profile.types";
import { ProfileUpdateFunction } from "../hooks/useMutations";
import { TFunction } from "i18next";

interface ProfileTextFieldProps {
  defaultValue: string;
  name: keyof Omit<Profile, "phoneNumber">;
  label: string;
  updateProfileField: ProfileUpdateFunction;
  allowBlank?: boolean;
}

const MAX_CHAR_LENGTH = 149;

const retrieveErrorMessage = ({
  t,
  value,
  different,
  allowBlank,
}: {
  t: TFunction;
  value: string;
  different: boolean;
  allowBlank?: boolean;
}) => {
  if (value.length > MAX_CHAR_LENGTH) {
    return t("profile.form.exceeds_max_length_error", {
      max_chars: MAX_CHAR_LENGTH,
    });
  }
  if (!allowBlank && value.length === 0 && different) {
    return t("profile.form.required_error");
  }
  return "";
};

const ProfileTextField = ({
  defaultValue,
  name,
  label,
  updateProfileField,
  allowBlank = false,
}: ProfileTextFieldProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue);
  const [errorMessage, setErrorMessage] = useState("");

  const onSubmit = (event: BaseSyntheticEvent) => {
    const target = event.target as HTMLInputElement | undefined;
    const rawValue = target?.value ?? "";
    const value = rawValue.trim();
    if (rawValue !== value) {
      setValue(value);
    }

    const different = value !== defaultValue;
    const translatedError = retrieveErrorMessage({
      t,
      value,
      different,
      allowBlank,
    });
    const shouldUpdate =
      different && (allowBlank || !!value) && !translatedError;

    setErrorMessage(translatedError);
    if (shouldUpdate) {
      void updateProfileField(name, value);
    }
  };

  return (
    <form onBlur={onSubmit} onSubmit={(event) => event.preventDefault()}>
      <TextField
        id={`profile-form-${name}`}
        name={name}
        label={label}
        value={value}
        handleOnChange={(_, value) => setValue(value)}
        hasError={!!errorMessage}
        errorMessage={errorMessage}
      />
    </form>
  );
};

export default ProfileTextField;
